/**
 * E002-T018: StreamLainSidebarSetup
 * sl-oll9.13: SRP — only sidebar tab + fullscreen view registration.
 * Event routing extracted to StreamLainEventRouter.
 */

import { I18n } from '@affine/i18n';
import {
  AttachmentIcon,
  DatabaseTableViewIcon,
  PlayIcon,
  SettingsIcon,
} from '@blocksuite/icons/rc';
import { Service } from '@toeverything/infra';
import React from 'react';

import { DatabaseFullscreenSwitchIcon } from '../../../blocksuite/block-suite-mode-switch/database-fullscreen-switch-icon';
import elsterIconWhiteUrl from '../../../desktop/dialogs/setting/workspace-setting/integration/erica/elster-icon-white.assets.svg';
import type { ExtensionSidebarService } from '../../workbench';
import { ensureBuiltInViewComponentsRegistered } from '../components/view-components/ensure-built-in-components';
import { ViewConfigFullscreenShell } from '../components/view-config-editor/shell';
import { isElsterEnabledFromBody } from '../hooks/use-elster-gate';
import { isNewsPortalEnabledFromBody } from '../hooks/use-news-portal-gate';
// sl-n8i1.5: Use gated panel that checks workflow engine configuration
import {
  AutomationConfigPanelGated,
  WorkspaceDatabasesPanel,
} from '../sidebar';
import { AttachmentFullscreenView } from '../sidebar/attachment-fullscreen-view';
import { DatabaseFullscreenView } from '../sidebar/database-fullscreen-view';
import { ElsterFullscreenView } from '../sidebar/elster-fullscreen-view';
import { NewsPortalFullscreenView } from '../sidebar/news-portal-fullscreen-view';
import { findBlockByFlavour } from '../utils/block-helpers';
import { findViewConfigBlockId } from '../utils/find-view-config-block-id';
import { ComponentRegistry } from './component-registry';
import { FullscreenRegistry } from './fullscreen-registry';

/**
 * sl-djze.3: Custom event name for navigating to a pending decision in the review panel.
 * Dispatched from PendingDecisionNotificationItem in the notification list.
 */
export const PENDING_DECISION_REVIEW_EVENT = 'pending-decision-review';

export interface PendingDecisionReviewDetail {
  pendingDecisionId: string;
}

/**
 * Registers StreamLain sidebar tabs and fullscreen views.
 * No event listener logic — that lives in StreamLainEventRouter.
 */
export class StreamLainSidebarSetup extends Service {
  constructor(
    private readonly extensionSidebarService: ExtensionSidebarService,
    private readonly fullscreenRegistry: FullscreenRegistry,
    private readonly componentRegistry: ComponentRegistry
  ) {
    super();

    // sl-9qm.4.3 / sl-2q8e.8: populate ComponentRegistry on workspace boot
    // so Custom App editor shell + renderer have view-components available.
    ensureBuiltInViewComponentsRegistered(this.componentRegistry);

    // E018-S010-T005: Register Workspace Databases tab (replaces old Tables tab)
    this.extensionSidebarService.register({
      id: 'streamlain-workspace-databases',
      label: I18n['com.streamlain.streamlain.workspace.databases'](),
      icon: React.createElement(DatabaseFullscreenSwitchIcon),
      component: WorkspaceDatabasesPanel,
      order: 45,
    });

    // Register Automation tab
    // sl-n8i1.5: Uses gated panel that shows placeholder when workflow engine not configured
    this.extensionSidebarService.register({
      id: 'streamlain-automation',
      label: I18n['com.streamlain.streamlain.workspace.automations'](),
      icon: React.createElement(PlayIcon),
      component: AutomationConfigPanelGated,
      order: 50, // After Workspace Databases, before Chat
    });

    // sl-yphr.14: Register ELSTER fullscreen view
    this.fullscreenRegistry.register({
      id: 'elster',
      icon: React.createElement('img', {
        src: elsterIconWhiteUrl,
        width: 16,
        height: 16,
        alt: 'ELSTER',
        style: { display: 'inline-block' },
      }),
      label: 'ELSTER',
      shortcut: 'Alt+T',
      component: ElsterFullscreenView,
      isAvailable: isElsterEnabledFromBody,
      findDefaultBlockId: findBlockByFlavour('affine:elster'),
      order: 30,
    });

    // sl-oll9.26: Register News Portal fullscreen view
    this.fullscreenRegistry.register({
      id: 'news-portal',
      icon: React.createElement(DatabaseTableViewIcon),
      label: 'News Portal',
      shortcut: 'Alt+N',
      component: NewsPortalFullscreenView,
      isAvailable: isNewsPortalEnabledFromBody,
      findDefaultBlockId: () => null,
      order: 26,
    });

    // sl-yphr.18: Register Database fullscreen view
    this.fullscreenRegistry.register({
      id: 'database',
      icon: React.createElement(DatabaseTableViewIcon),
      label: 'Database',
      shortcut: 'Alt+D',
      component: DatabaseFullscreenView,
      isAvailable: () => true,
      findDefaultBlockId: findBlockByFlavour('affine:database'),
      order: 10,
    });

    this.fullscreenRegistry.register({
      id: 'attachment',
      icon: React.createElement(AttachmentIcon),
      label: 'Attachment',
      component: AttachmentFullscreenView,
      hideModalClose: true,
      isAvailable: () => true,
      findDefaultBlockId: findBlockByFlavour('affine:attachment'),
      order: 12,
    });

    // sl-8n5m.5.1: Register ViewConfig fullscreen view. The button is
    // always available; findDefaultBlockId returns null when no ViewConfig
    // database exists yet — the shell's empty state (sl-8n5m.5.2) handles
    // the create-on-demand flow.
    this.fullscreenRegistry.register({
      id: 'view-config',
      icon: React.createElement(SettingsIcon),
      label: 'Custom App Configuration',
      shortcut: 'Alt+V',
      component: ViewConfigFullscreenShell,
      isAvailable: () => true,
      findDefaultBlockId: findViewConfigBlockId,
      order: 30,
    });
  }
}
